import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  Bold,
  Check,
  ChevronDown,
  Code,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Pilcrow,
  Quote,
  Strikethrough,
  Underline as UnderlineIcon,
} from 'lucide-react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useEditorState, type Editor } from '@tiptap/react';
import { useState, type ComponentType, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface Props {
  editor: Editor | null;
}

interface BtnProps {
  active?: boolean;
  onClick: () => void;
  ariaLabel: string;
  hint?: string;
  children: ReactNode;
}

function BubbleBtn({ active = false, onClick, ariaLabel, hint, children }: BtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={active}
      title={hint !== undefined ? `${ariaLabel} · ${hint}` : ariaLabel}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-full transition-all duration-150 motion-reduce:transition-none',
        'focus-visible:ring-primary-200 focus-visible:ring-2 focus-visible:outline-none',
        active
          ? 'bg-primary-50 text-accent-ink'
          : 'hover:text-accent-ink text-neutral-600 hover:bg-neutral-100 active:bg-neutral-200/70',
      )}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span aria-hidden className="bg-accent-ink/10 mx-1 h-5 w-px shrink-0" />;
}

interface HighlightColor {
  /** undefined = default mint (no `data-color` attribute on the `<mark>`). */
  id: 'yellow' | 'rose' | 'ink' | undefined;
  label: string;
  /** Full-fidelity swatch matching the gradient in `globals.css`. */
  swatchClass: string;
}

const DEFAULT_HIGHLIGHT: HighlightColor = {
  id: undefined,
  label: 'Mint',
  swatchClass: 'bg-[rgba(178,220,200,0.7)]',
};

const HIGHLIGHT_COLORS: HighlightColor[] = [
  DEFAULT_HIGHLIGHT,
  { id: 'yellow', label: 'Yellow', swatchClass: 'bg-[rgba(244,211,94,0.65)]' },
  { id: 'rose', label: 'Rose', swatchClass: 'bg-[rgba(244,178,178,0.6)]' },
  { id: 'ink', label: 'Ink', swatchClass: 'bg-[rgba(3,68,92,0.18)]' },
];

interface SelectorState {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrike: boolean;
  isCode: boolean;
  isLink: boolean;
  isHighlight: boolean;
  highlightColor: string | undefined;
  blockLabel: string;
  isParagraph: boolean;
  isH2: boolean;
  isH3: boolean;
  isQuote: boolean;
  isBulletList: boolean;
  isOrderedList: boolean;
}

// Shared content + item recipes. We use Radix primitives directly here
// (rather than the shadcn-wrapped DropdownMenuContent) so we can pass
// `container={bubbleEl}` to the Portal — see the comment on the
// `bubbleEl` state for why that's load-bearing.
const dropdownContentClasses = cn(
  'bg-popover text-popover-foreground border-(--color-border) z-50 min-w-40 origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-lg border p-1 shadow-lg',
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
);

const dropdownItemClasses = cn(
  'focus:bg-accent focus:text-accent-foreground text-accent-ink relative flex min-h-9 cursor-default items-center gap-2 rounded-md px-2.5 py-1.5 text-sm outline-none select-none transition-colors',
  'data-disabled:pointer-events-none data-disabled:opacity-50',
);

const dropdownLabelClasses =
  'font-display tracking-eyebrow text-accent-ink px-2 py-1.5 text-[10px] font-medium uppercase italic';

export function BubbleToolbar({ editor }: Props) {
  // The bubble's outer wrapper is captured into state (not a ref) on purpose.
  // We pass this as the Portal `container` on every dropdown so the dropdown
  // content renders INSIDE the bubble's DOM tree (not in document.body).
  // That matters because BubbleMenu's blurHandler hides the bubble unless
  // the new focus target is inside `bubble.element.parentNode`. With the
  // dropdown in document.body, focus moves outside the bubble's parent,
  // the bubble gets removed, and Radix Popper drops the dropdown at (0,0).
  //
  // useState (not useRef) so that when the callback ref attaches on mount,
  // state updates and the component re-renders with the resolved container.
  // A plain useRef would still be `null` on first render and the Portal
  // would silently fall back to document.body — exactly what we're trying
  // to avoid. (This is what the react-hooks/refs lint rule guards against.)
  const [bubbleEl, setBubbleEl] = useState<HTMLDivElement | null>(null);

  const state = useEditorState({
    editor,
    selector: ({ editor: ed }): SelectorState | null => {
      if (ed === null) return null;
      const isH2 = ed.isActive('heading', { level: 2 });
      const isH3 = ed.isActive('heading', { level: 3 });
      const isQuote = ed.isActive('blockquote');
      const isBulletList = ed.isActive('bulletList');
      const isOrderedList = ed.isActive('orderedList');
      const isHeading = ed.isActive('heading');
      const blockLabel = isH2
        ? 'Heading 2'
        : isH3
          ? 'Heading 3'
          : isQuote
            ? 'Quote'
            : isBulletList
              ? 'Bullet list'
              : isOrderedList
                ? 'Numbered list'
                : 'Text';
      const highlightAttrs = ed.getAttributes('highlight') as { color?: string };
      return {
        isBold: ed.isActive('bold'),
        isItalic: ed.isActive('italic'),
        isUnderline: ed.isActive('underline'),
        isStrike: ed.isActive('strike'),
        isCode: ed.isActive('code'),
        isLink: ed.isActive('link'),
        isHighlight: ed.isActive('highlight'),
        highlightColor: highlightAttrs.color,
        blockLabel,
        isParagraph: !isHeading && !isQuote && !isBulletList && !isOrderedList,
        isH2,
        isH3,
        isQuote,
        isBulletList,
        isOrderedList,
      };
    },
  });

  if (editor === null || state === null) return null;

  const currentHighlight: HighlightColor =
    HIGHLIGHT_COLORS.find((c) => c.id === state.highlightColor) ?? DEFAULT_HIGHLIGHT;

  const blockOptions: {
    label: string;
    Icon: ComponentType<{ className?: string; strokeWidth?: number }>;
    isActive: boolean;
    run: () => void;
  }[] = [
    {
      label: 'Paragraph',
      Icon: Pilcrow,
      isActive: state.isParagraph,
      run: () => editor.chain().focus().setParagraph().run(),
    },
    {
      label: 'Heading 2',
      Icon: Heading2,
      isActive: state.isH2,
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      label: 'Heading 3',
      Icon: Heading3,
      isActive: state.isH3,
      run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      label: 'Quote',
      Icon: Quote,
      isActive: state.isQuote,
      run: () => editor.chain().focus().toggleBlockquote().run(),
    },
    {
      label: 'Bullet list',
      Icon: List,
      isActive: state.isBulletList,
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'Numbered list',
      Icon: ListOrdered,
      isActive: state.isOrderedList,
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
  ];

  const promptLink = (): void => {
    const previousUrl = (editor.getAttributes('link').href as string | undefined) ?? '';
    const url = window.prompt('Link URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor: ed, from, to }) => {
        return !ed.isDestroyed && from !== to && ed.isEditable;
      }}
    >
      <div
        ref={setBubbleEl}
        role="toolbar"
        aria-label="Selection formatting"
        className={cn(
          'ring-accent-ink/8 inline-flex items-center gap-0.5 rounded-full bg-white p-1 ring-1',
          'shadow-[0_8px_24px_-6px_rgba(3,68,92,0.18),0_2px_6px_-2px_rgba(3,68,92,0.08)]',
          'animate-in fade-in zoom-in-95 duration-150 ease-out motion-reduce:animate-none',
        )}
      >
        {/* Block-style dropdown — Radix primitives so we can route the Portal
            container into the bubble itself (see bubbleEl comment). */}
        <DropdownMenuPrimitive.Root>
          <DropdownMenuPrimitive.Trigger
            aria-label="Block style"
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full px-3 transition-colors',
              'text-accent-ink hover:bg-neutral-100',
              'focus-visible:ring-primary-200 focus-visible:ring-2 focus-visible:outline-none',
            )}
          >
            <span className="font-display text-[13px] italic">{state.blockLabel}</span>
            <ChevronDown className="h-3 w-3 opacity-60" strokeWidth={1.75} />
          </DropdownMenuPrimitive.Trigger>
          <DropdownMenuPrimitive.Portal container={bubbleEl}>
            <DropdownMenuPrimitive.Content
              align="start"
              sideOffset={8}
              className={cn(dropdownContentClasses, 'w-44')}
            >
              <DropdownMenuPrimitive.Label className={dropdownLabelClasses}>
                Block
              </DropdownMenuPrimitive.Label>
              {blockOptions.map((opt) => {
                const { Icon } = opt;
                return (
                  <DropdownMenuPrimitive.Item
                    key={opt.label}
                    onSelect={opt.run}
                    className={cn(dropdownItemClasses, 'flex items-center justify-between gap-2')}
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span
                        aria-hidden
                        className={cn(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors',
                          opt.isActive
                            ? 'border-primary-200/70 bg-primary-50 text-accent-ink'
                            : 'border-accent-ink/8 bg-neutral-50 text-neutral-600',
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </span>
                      <span
                        className={cn(
                          'truncate text-[13px]',
                          opt.isActive ? 'text-accent-ink font-medium' : 'text-neutral-800',
                        )}
                      >
                        {opt.label}
                      </span>
                    </span>
                    {opt.isActive ? (
                      <Check className="text-primary-500 h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                    ) : null}
                  </DropdownMenuPrimitive.Item>
                );
              })}
            </DropdownMenuPrimitive.Content>
          </DropdownMenuPrimitive.Portal>
        </DropdownMenuPrimitive.Root>

        <Sep />

        <BubbleBtn
          ariaLabel="Bold"
          hint="⌘B"
          active={state.isBold}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" strokeWidth={2.25} />
        </BubbleBtn>
        <BubbleBtn
          ariaLabel="Italic"
          hint="⌘I"
          active={state.isItalic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" strokeWidth={1.75} />
        </BubbleBtn>
        <BubbleBtn
          ariaLabel="Underline"
          hint="⌘U"
          active={state.isUnderline}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
        </BubbleBtn>
        <BubbleBtn
          ariaLabel="Strikethrough"
          active={state.isStrike}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-3.5 w-3.5" strokeWidth={1.75} />
        </BubbleBtn>

        {/* Highlight palette — same Portal-into-bubble trick as the block dropdown. */}
        <DropdownMenuPrimitive.Root>
          <DropdownMenuPrimitive.Trigger
            aria-label="Highlight color"
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full px-2 transition-colors',
              'focus-visible:ring-primary-200 focus-visible:ring-2 focus-visible:outline-none',
              state.isHighlight
                ? 'bg-primary-50 text-accent-ink'
                : 'hover:text-accent-ink text-neutral-600 hover:bg-neutral-100',
            )}
          >
            <Highlighter className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span
              aria-hidden
              className={cn(
                'ring-accent-ink/15 h-3 w-3 rounded-full ring-1',
                currentHighlight.swatchClass,
              )}
            />
            <ChevronDown className="h-3 w-3 opacity-60" strokeWidth={1.75} />
          </DropdownMenuPrimitive.Trigger>
          <DropdownMenuPrimitive.Portal container={bubbleEl}>
            <DropdownMenuPrimitive.Content
              align="center"
              sideOffset={8}
              className={cn(dropdownContentClasses, 'w-44')}
            >
              <DropdownMenuPrimitive.Label className={dropdownLabelClasses}>
                Highlight
              </DropdownMenuPrimitive.Label>
              {HIGHLIGHT_COLORS.map((color) => {
                const isCurrent = state.isHighlight && currentHighlight.id === color.id;
                return (
                  <DropdownMenuPrimitive.Item
                    key={color.label}
                    onSelect={() => {
                      if (color.id === undefined) {
                        editor.chain().focus().unsetHighlight().setHighlight().run();
                      } else {
                        editor.chain().focus().setHighlight({ color: color.id }).run();
                      }
                    }}
                    className={cn(dropdownItemClasses, 'flex items-center justify-between')}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className={cn(
                          'ring-accent-ink/15 h-4 w-4 rounded-full ring-1',
                          color.swatchClass,
                        )}
                      />
                      <span className="text-[13px]">{color.label}</span>
                    </span>
                    {isCurrent ? (
                      <Check className="text-primary-500 h-3.5 w-3.5" strokeWidth={2.5} />
                    ) : null}
                  </DropdownMenuPrimitive.Item>
                );
              })}
              {state.isHighlight ? (
                <>
                  <DropdownMenuPrimitive.Separator className="bg-muted -mx-1 my-1 h-px" />
                  <DropdownMenuPrimitive.Item
                    onSelect={() => editor.chain().focus().unsetHighlight().run()}
                    className={cn(dropdownItemClasses, 'text-[13px] text-neutral-600')}
                  >
                    Remove highlight
                  </DropdownMenuPrimitive.Item>
                </>
              ) : null}
            </DropdownMenuPrimitive.Content>
          </DropdownMenuPrimitive.Portal>
        </DropdownMenuPrimitive.Root>

        <Sep />

        <BubbleBtn
          ariaLabel="Inline code"
          active={state.isCode}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <Code className="h-3.5 w-3.5" strokeWidth={1.75} />
        </BubbleBtn>
        <BubbleBtn ariaLabel="Insert link" active={state.isLink} onClick={promptLink}>
          <LinkIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
        </BubbleBtn>
      </div>
    </BubbleMenu>
  );
}
